import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { agent } from 'services/service';
import {
  AttendanceDetailDayStatus,
  AttendanceEmployeeComplianceStatus,
  EmployeeAttendanceDetailResponse
} from 'services/AttendanceTrackingService';
import {
  AttendanceDashboardPeriod,
  formatDashboardPeriodQuery,
  formatDashboardPeriodRange,
  getLastMonthPeriod,
  isFullMonthDashboardPeriod,
  parsePeriodFromSearchParams,
  periodToSearchParams
} from 'utils/attendanceDashboardPeriod';
import {
  buildManagerAttendanceCalendarPath,
  buildManagerAttendanceListPath
} from 'utils/managerPortalPaths';
import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import { formatLeaveCount } from 'utils/formatAttendanceEmployee';
import {
  buildAttendanceStatusLegend,
  DEFAULT_FULL_DAY_HOURS,
  DEFAULT_SHORT_DAY_HOURS,
  getAttendanceStatusClass
} from 'utils/formatAttendanceDashboard';
import '../../AttendanceEmployeeDetail/index.scss';

type DetailDayStatus = AttendanceDetailDayStatus;

interface AttendanceRecord {
  id: string;
  date: string;
  arrival: string;
  departure: string;
  hours: string;
  status: DetailDayStatus;
  isCorrected: boolean;
  correctionNote: string;
  correctedByName: string;
  correctedAt: string;
}

interface EmployeeDetail {
  epfNumber: string;
  name: string;
  department: string;
  employmentType: string;
  complianceStatus: AttendanceEmployeeComplianceStatus;
  officeDays: number;
  workingDaysInPeriod: number;
  minimumOfficeDays: number;
  leaveDays: number;
  fullDayLeaveCount: number;
  halfDayLeaveCount: number;
  missingRecords: number;
  wfhDays: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  records: AttendanceRecord[];
}

const STATUS_CLASS: Record<DetailDayStatus, string> = {
  Present: 'present',
  ShortDay: 'short-day',
  VeryShort: 'very-short',
  Missing: 'missing',
  WFH: 'wfh',
  FullDayLeave: 'full-day-leave',
  HalfDayLeave: 'half-day-leave'
};

const DETAIL_STATUS_SHORT_LABELS: Record<DetailDayStatus, string> = {
  Present: 'Present',
  ShortDay: 'Short Day',
  VeryShort: 'Too Short',
  Missing: 'Missing',
  WFH: 'WFH',
  FullDayLeave: 'Full Day Leave',
  HalfDayLeave: 'Half Day Leave'
};

const LEGEND_WFH_ITEM = { className: 'wfh', label: 'WFH' } as const;

const DETAIL_STATUS_FILTER_VALUES = [
  'Present',
  'ShortDay',
  'VeryShort',
  'Missing',
  'WFH',
  'FullDayLeave',
  'HalfDayLeave'
] as const satisfies readonly DetailDayStatus[];

const RECORDS_PAGE_SIZE = 100;
const LOAD_MORE_THRESHOLD_PX = 120;

export const ManagerAttendanceDetail = () => {
  const navigate = useNavigate();
  const { epfNumber = '' } = useParams<{ epfNumber: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | DetailDayStatus>(
    'all'
  );
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    parsePeriodFromSearchParams(searchParams, getLastMonthPeriod())
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLTableRowElement>(null);
  const onLoadMoreRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    setStatusFilter('all');
    setPeriod(parsePeriodFromSearchParams(searchParams, getLastMonthPeriod()));
  }, [epfNumber, searchParams]);

  const handlePeriodChange = useCallback(
    (nextPeriod: AttendanceDashboardPeriod) => {
      setPeriod(nextPeriod);
      setSearchParams(periodToSearchParams(nextPeriod), { replace: true });
    },
    [setSearchParams]
  );

  const employeesListPath = useMemo(
    () => buildManagerAttendanceListPath(period),
    [period]
  );

  const mapRecords = (
    response: EmployeeAttendanceDetailResponse
  ): AttendanceRecord[] =>
    response.records.map((r) => ({
      id: `${response.epfNumber}-${r.date}`,
      date: r.date,
      arrival: r.arrivalTime ? r.arrivalTime.slice(0, 5) : '—',
      departure: r.departureTime ? r.departureTime.slice(0, 5) : '—',
      hours: r.hoursWorked ?? '—',
      status: r.status,
      isCorrected: r.isCorrected,
      correctionNote: r.correctionNote?.trim() ?? '',
      correctedByName: r.correctedByName?.trim() ?? '',
      correctedAt: r.correctedAt ?? ''
    }));

  const mapEmployeeDetail = (
    response: EmployeeAttendanceDetailResponse
  ): EmployeeDetail => ({
    epfNumber: response.epfNumber,
    name: response.employeeFullName,
    department: response.department ?? '—',
    employmentType: response.employmentType ?? '—',
    complianceStatus: response.isCompliant ? 'Compliant' : 'BelowThreshold',
    officeDays: response.officeDays,
    workingDaysInPeriod:
      response.workingDaysInPeriod ?? response.totalRecords,
    minimumOfficeDays: response.requiredOfficeDays,
    leaveDays: response.leaveDays,
    fullDayLeaveCount: response.fullDayLeaveCount ?? 0,
    halfDayLeaveCount: response.halfDayLeaveCount ?? 0,
    missingRecords: response.missingRecords,
    wfhDays: response.wfhDays,
    fullDayHoursThreshold:
      response.fullDayHoursThreshold ?? DEFAULT_FULL_DAY_HOURS,
    shortDayHoursThreshold:
      response.shortDayHoursThreshold ?? DEFAULT_SHORT_DAY_HOURS,
    records: mapRecords(response)
  });

  const fetchDetail = useCallback(
    async (pageToFetch: number, append: boolean) => {
      const { from, to } = formatDashboardPeriodQuery(period);
      const response =
        await agent.AttendanceTrackingService.getEmployeeAttendanceDetail(
          epfNumber,
          from,
          to,
          undefined,
          pageToFetch,
          RECORDS_PAGE_SIZE
        );
      const mappedRecords = mapRecords(response.data);

      if (append) {
        setRecords((current) => [...current, ...mappedRecords]);
      } else {
        setEmployee(mapEmployeeDetail(response.data));
        setRecords(mappedRecords);
      }

      setTotalRecords(response.data.totalRecords);
      pageRef.current = response.data.page;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [epfNumber, period]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    pageRef.current = 1;

    try {
      await fetchDetail(1, false);
    } catch {
      setEmployee(null);
      setRecords([]);
      setTotalRecords(0);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, [fetchDetail]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return;
    if (records.length >= totalRecords) return;

    setLoadingMore(true);

    try {
      await fetchDetail(pageRef.current + 1, true);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchDetail, loading, loadingMore, records.length, totalRecords]);

  onLoadMoreRef.current = loadMore;

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const hasMore = records.length < totalRecords;

  const filteredRecords = useMemo(() => {
    if (statusFilter === 'all') return records;
    return records.filter((record) => record.status === statusFilter);
  }, [records, statusFilter]);

  const shouldLoadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return false;

    const root = scrollRef.current;
    if (!root) return false;

    const distanceFromBottom =
      root.scrollHeight - root.scrollTop - root.clientHeight;

    return distanceFromBottom <= LOAD_MORE_THRESHOLD_PX;
  }, [hasMore, loading, loadingMore]);

  const tryLoadMore = useCallback(() => {
    if (shouldLoadMore()) {
      onLoadMoreRef.current();
    }
  }, [shouldLoadMore]);

  const fillScrollContainerIfNeeded = useCallback(() => {
    const root = scrollRef.current;
    if (!root || !hasMore || loading || loadingMore) return;

    if (root.scrollHeight <= root.clientHeight + 4) {
      onLoadMoreRef.current();
    }
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = loadMoreRef.current;

    if (!root || !target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        root,
        rootMargin: `${LOAD_MORE_THRESHOLD_PX}px`,
        threshold: 0
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, records.length]);

  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const runLayoutChecks = () => {
      if (cancelled) return;
      tryLoadMore();
      fillScrollContainerIfNeeded();
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(runLayoutChecks);
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && scrollRef.current
        ? new ResizeObserver(runLayoutChecks)
        : null;

    if (resizeObserver && scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    const layoutTimer = window.setTimeout(runLayoutChecks, 300);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      clearTimeout(layoutTimer);
      resizeObserver?.disconnect();
    };
  }, [
    loading,
    loadingMore,
    hasMore,
    records.length,
    tryLoadMore,
    fillScrollContainerIfNeeded
  ]);

  const handleTableScroll = useCallback(() => {
    tryLoadMore();
  }, [tryLoadMore]);

  const fullDayHours =
    employee?.fullDayHoursThreshold ?? DEFAULT_FULL_DAY_HOURS;
  const shortDayHours =
    employee?.shortDayHoursThreshold ?? DEFAULT_SHORT_DAY_HOURS;

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'All statuses' },
      ...DETAIL_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: DETAIL_STATUS_SHORT_LABELS[value]
      }))
    ],
    []
  );

  const renderStatusBadge = (status: DetailDayStatus) => (
    <span
      className={`attendance-dashboard__status-text attendance-dashboard__status-text--${STATUS_CLASS[status]}`}
    >
      {DETAIL_STATUS_SHORT_LABELS[status]}
    </span>
  );

  const renderRecordStatus = (record: AttendanceRecord) => (
    <div className="attendance-employee-detail-page__status-cell">
      {renderStatusBadge(record.status)}
      {record.isCorrected ? (
        <span className="attendance-employee-detail-page__corrected-label">
          Corrected
        </span>
      ) : null}
    </div>
  );

  const statusLegend = useMemo(() => {
    const hourBased = buildAttendanceStatusLegend(fullDayHours, shortDayHours);
    return [
      ...hourBased.map((item) => ({
        className: getAttendanceStatusClass(item.status),
        label: item.label
      })),
      LEGEND_WFH_ITEM
    ];
  }, [fullDayHours, shortDayHours]);

  if (loading) {
    return (
      <div className="attendance-employee-detail-page">
        <div className="attendance-employee-detail-page__not-found">
          Loading employee attendance...
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="attendance-employee-detail-page">
        <div className="attendance-employee-detail-page__not-found">
          {error ?? 'Employee not found.'}
        </div>
      </div>
    );
  }

  const isCompliant = employee.complianceStatus === 'Compliant';
  const isFullMonth = isFullMonthDashboardPeriod(period);
  const officeDayProgress = Math.min(
    100,
    employee.minimumOfficeDays > 0
      ? (employee.officeDays / employee.minimumOfficeDays) * 100
      : 0
  );

  return (
    <div className="attendance-employee-detail-page">
      <AttendanceTrackingPageHeader
        title={employee.name}
        description={`${employee.department} · EPF ${employee.epfNumber} · ${employee.employmentType}`}
        descriptionSingleLine
        subDescription={`Showing data for ${formatDashboardPeriodRange(period)}`}
        titleLeading={
          <button
            type="button"
            className="attendance-employee-detail-page__back-icon-btn"
            aria-label="Back to Employee Attendance"
            onClick={() => navigate(employeesListPath)}
          >
            <ChevronLeftIcon fontSize="small" aria-hidden />
          </button>
        }
        titleAction={
          <button
            type="button"
            className="attendance-employee-detail-page__calendar-btn"
            onClick={() =>
              navigate(
                buildManagerAttendanceCalendarPath(employee.epfNumber, period)
              )
            }
          >
            <CalendarMonthOutlinedIcon fontSize="small" aria-hidden />
            View Calendar
          </button>
        }
        crumbs={[
          { label: 'Manager Portal', to: '/manager/attendance' },
          {
            label: 'Employee Attendance',
            to: employeesListPath
          },
          { label: employee.name }
        ]}
      />

      {isFullMonth && (
        <div
          className={`attendance-employee-detail-page__compliance-banner attendance-employee-detail-page__compliance-banner--${isCompliant ? 'compliant' : 'below'}`}
        >
            <div className="attendance-employee-detail-page__compliance-icon">
              {isCompliant ? (
                <CheckCircleOutlineIcon fontSize="inherit" aria-hidden />
              ) : (
                <HighlightOffOutlinedIcon fontSize="inherit" aria-hidden />
              )}
            </div>
            <div className="attendance-employee-detail-page__compliance-body">
              <p className="attendance-employee-detail-page__compliance-line">
                <span className="attendance-employee-detail-page__compliance-title">
                  {isCompliant ? 'Compliant' : 'Below threshold'}
                </span>
                {' — '}
                <strong>{employee.name}</strong> has completed{' '}
                <strong>{employee.officeDays}</strong> office{' '}
                {employee.officeDays === 1 ? 'day' : 'days'} · Minimum
                required: <strong>{employee.minimumOfficeDays} days</strong>
              </p>
              <div
                className="attendance-employee-detail-page__compliance-progress"
                role="progressbar"
                aria-valuenow={employee.officeDays}
                aria-valuemin={0}
                aria-valuemax={employee.minimumOfficeDays}
                aria-label={`Office days progress: ${employee.officeDays} of ${employee.minimumOfficeDays}`}
              >
                <div
                  className="attendance-employee-detail-page__compliance-progress-bar"
                  style={{ width: `${officeDayProgress}%` }}
                />
              </div>
            </div>
          </div>
      )}

      <div className="attendance-employee-detail-page__metrics">
        <div
          className={`attendance-employee-detail-page__metric attendance-employee-detail-page__metric--office-${isCompliant ? 'ok' : 'low'}`}
        >
          <span className="attendance-employee-detail-page__metric-label">
            Work from Office Days
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.officeDays}/{employee.workingDaysInPeriod}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--full-leave">
          <span className="attendance-employee-detail-page__metric-label">
            Full Day Leave
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {formatLeaveCount(employee.fullDayLeaveCount)}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--half-leave">
          <span className="attendance-employee-detail-page__metric-label">
            Half Day Leave
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {formatLeaveCount(employee.halfDayLeaveCount)}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--missing">
          <span className="attendance-employee-detail-page__metric-label">
            Missing Records
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.missingRecords}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--wfh">
          <span className="attendance-employee-detail-page__metric-label">
            WFH Days
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.wfhDays}
          </span>
        </div>
      </div>

      <section className="attendance-employee-detail-page__panel">
        <h2 className="attendance-employee-detail-page__panel-title">
          Daily Records
        </h2>

        <div className="attendance-employee-detail-page__legend">
          {statusLegend.map((item) => (
            <span
              key={item.className}
              className={`attendance-employee-detail-page__legend-item attendance-employee-detail-page__legend-item--${item.className}`}
            >
              {item.label}
            </span>
          ))}
        </div>

        <div className="attendance-employee-detail-page__filters">
          <label className="attendance-employee-detail-page__filter-label">
            Status
            <select
              className="attendance-employee-detail-page__select"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as 'all' | DetailDayStatus);
              }}
            >
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="attendance-employee-detail-page__filter-spacer">
            <AttendanceDashboardPeriodFilter
              period={period}
              onChange={handlePeriodChange}
            />
          </div>
        </div>

        <div
          ref={scrollRef}
          className="attendance-employee-detail-page__table-wrap"
          onScroll={handleTableScroll}
        >
          <table className="attendance-employee-detail-page__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Arrival</th>
                <th>Departure</th>
                <th>Hours</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                <>
                  {filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td>{record.date}</td>
                      <td>{record.arrival}</td>
                      <td>{record.departure}</td>
                      <td>{record.hours}</td>
                      <td>{renderRecordStatus(record)}</td>
                    </tr>
                  ))}
                  {hasMore && statusFilter === 'all' && (
                    <tr
                      ref={loadMoreRef}
                      className="attendance-employee-detail-page__load-more-row"
                    >
                      <td colSpan={5}>
                        <span className="attendance-employee-detail-page__load-more">
                          {loadingMore ? (
                            <>
                              <CircularProgress size={18} />
                              Loading more records...
                            </>
                          ) : (
                            'Scroll for more'
                          )}
                        </span>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={5}>
                    {records.length > 0
                      ? 'No records match this status.'
                      : 'No records for this period.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <footer className="attendance-employee-detail-page__footer">
          <span className="attendance-employee-detail-page__footer-meta">
            {totalRecords === 0
              ? 'Showing 0 records'
              : statusFilter === 'all'
                ? `Showing ${records.length} of ${totalRecords} records`
                : `Showing ${filteredRecords.length} of ${records.length} records (${DETAIL_STATUS_SHORT_LABELS[statusFilter]})`}
          </span>
        </footer>
      </section>
    </div>
  );
};
